import React, { useState, useRef, useCallback } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { isSessionValid, getSessionGroups } from '../service/CognitoAuthService';
import { APP_ROUTES } from '../constants/app-routes';

export default function ProtectedRoute({ authGroups, redirectPath }) {
  const [isLoading, setIsLoading] = useState(true);
  const isAuthorized = useRef(false);

  const checkAuthorizedUser = useCallback(async () => {
    try {
      const isValidC = await isSessionValid();
      if (isValidC) {
        const groups = await getSessionGroups();
        if (groups && groups.length && groups.some((g) => authGroups.includes(g))) {
          isAuthorized.current = true;
        }
      }
    } catch {
      isAuthorized.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [authGroups]);

  React.useEffect(() => {
    checkAuthorizedUser();
  }, [checkAuthorizedUser]);

  if (isLoading) {
    return (
      <div>
        <Box sx={{ width: '100%' }}>Loading page...</Box>
      </div>
    );
  }

  if (!isAuthorized.current) {
    // Single, clean redirect (replace so it does not stack history entries and
    // cause the back-and-forth flicker the render-phase navigate() produced).
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
}

ProtectedRoute.propTypes = {
  authGroups: PropTypes.arrayOf.isRequired,
  redirectPath: PropTypes.string,
};

ProtectedRoute.defaultProps = {
  // Default to the landing/login page. An unauthorized user should never be
  // sent to the dashboard (the previous '/app/dashboard' default fed the loop).
  redirectPath: APP_ROUTES.HOME,
};
